import { TalentNode, UNIFIED_TALENT_TREE, isTalentUnlocked } from '../data/unifiedTalentTree';

interface TimeStats {
  weeklyCommitment: number;
  totalSpentTime: number;
  availableTime: number;
}

// Simplified interface for storage (without React components)
interface SavedTalentState {
  id: string;
  currentSession: number;
  unlocked: boolean;
}

// Weekly commitment selection for recommendations
export interface TalentRecommendation {
  talent: TalentNode;
  suggestedSessions: number;
  totalMinutes: number;
  reason: 'high-priority' | 'time-fill' | 'user-selected';
}

class UnifiedTalentTreeService {
  private static instance: UnifiedTalentTreeService;
  private listeners: ((stats: TimeStats) => void)[] = [];

  static getInstance(): UnifiedTalentTreeService {
    if (!UnifiedTalentTreeService.instance) {
      UnifiedTalentTreeService.instance = new UnifiedTalentTreeService();
    }
    return UnifiedTalentTreeService.instance;
  }

  // Initialize the unified tree with saved state
  initializeTree(): TalentNode[] {
    const savedState = this.getSavedState();
    const tree = [...UNIFIED_TALENT_TREE];

    if (savedState && savedState.length > 0) {
      // Apply saved state to tree
      savedState.forEach(savedTalent => {
        const talent = tree.find(t => t.id === savedTalent.id);
        if (talent) {
          talent.currentSession = savedTalent.currentSession;
          talent.unlocked = savedTalent.unlocked;
        }
      });
    }

    // Update unlock status based on current state
    this.updateUnlockStatus(tree);
    return tree;
  }

  // Update unlock status for all talents based on prerequisites
  private updateUnlockStatus(tree: TalentNode[]): void {
    tree.forEach(talent => {
      if (!talent.unlocked && talent.requires) {
        let canUnlock: boolean;
        
        // Handle both simple array (AND) and array of arrays (OR groups)
        if (Array.isArray(talent.requires[0])) {
          // OR condition: at least one group must be satisfied
          canUnlock = (talent.requires as string[][]).some(group => 
            group.every(reqId => {
              const reqTalent = tree.find(t => t.id === reqId);
              if (!reqTalent) return false;
              
              // Special case: Health Tree is always available to unlock dependents
              if (reqTalent.id === 'health-tree') {
                return reqTalent.unlocked;
              }
              
              // All other talents require actual point investment
              return reqTalent.currentSession > 0;
            })
          );
        } else {
          // AND condition: all requirements must be satisfied
          canUnlock = (talent.requires as string[]).every(reqId => {
            const reqTalent = tree.find(t => t.id === reqId);
            if (!reqTalent) return false;
            
            // Special case: Health Tree is always available to unlock dependents
            if (reqTalent.id === 'health-tree') {
              return reqTalent.unlocked;
            }
            
            // All other talents require actual point investment
            return reqTalent.currentSession > 0;
          });
        }
        
        if (canUnlock) {
          talent.unlocked = true;
        }
      }
    });
  }

  // Get current talent tree state
  getTree(): TalentNode[] {
    return this.initializeTree();
  }

  // Upgrade talent (add session)
  upgradeTalent(talentId: string): boolean {
    const tree = this.getTree();
    const talent = tree.find(t => t.id === talentId);
    
    if (!talent || !talent.unlocked || talent.currentSession >= talent.maxSession) {
      return false;
    }

    talent.currentSession++;
    this.saveState(tree);
    this.updateUnlockStatus(tree);
    this.saveState(tree); // Save again after unlock updates
    this.notifyListeners();
    return true;
  }

  // Downgrade talent (remove session)
  downgradeTalent(talentId: string): boolean {
    const tree = this.getTree();
    const talent = tree.find(t => t.id === talentId);
    
    if (!talent || talent.currentSession <= 0) {
      return false;
    }

    // Check if downgrading would break dependencies
    const dependentTalents = tree.filter(t => {
      if (!t.requires || t.currentSession === 0) return false;
      
      // Handle both simple array (AND) and array of arrays (OR groups)
      if (Array.isArray(t.requires[0])) {
        // OR condition: check if this talent is the only satisfied prerequisite
        const satisfiedGroups = (t.requires as string[][]).filter(group =>
          group.every(reqId => {
            const reqTalent = tree.find(talent => talent.id === reqId);
            return reqTalent && (reqTalent.id === 'health-tree' ? reqTalent.unlocked : reqTalent.currentSession > 0);
          })
        );
        
        // If only one group is satisfied and it contains this talent, check if removing it breaks the dependency
        if (satisfiedGroups.length === 1) {
          return satisfiedGroups[0].includes(talentId);
        }
        return false;
      } else {
        // AND condition: if this talent is required, it's a dependency
        return (t.requires as string[]).includes(talentId);
      }
    });

    if (dependentTalents.length > 0 && talent.currentSession === 1) {
      // Cannot downgrade if it would break dependencies
      return false;
    }

    talent.currentSession--;
    this.saveState(tree);
    this.updateUnlockStatus(tree);
    this.saveState(tree);
    this.notifyListeners();
    return true;
  }

  // Get time-based talent recommendations
  getRecommendations(availableMinutes: number): TalentRecommendation[] {
    const tree = this.getTree();
    const recommendations: TalentRecommendation[] = [];
    let remainingMinutes = availableMinutes;

    // Check if ALL longevity talents are maxed out
    const longevityTalents = tree.filter(t => t.category === 'longevity' && t.id !== 'health-tree');
    const allLongevityMaxed = longevityTalents.every(t => t.currentSession >= t.maxSession);
    
    console.log(`DEBUG: Longevity status - ${longevityTalents.filter(t => t.currentSession >= t.maxSession).length}/${longevityTalents.length} maxed out. All maxed: ${allLongevityMaxed}`);

    // Filter available talents based on longevity completion
    const availableTalents = tree.filter(talent => {
      // Skip health-tree (special case)
      if (talent.id === 'health-tree') return false;
      
      // If longevity talents are NOT all maxed, ONLY allow longevity talents
      if (!allLongevityMaxed && talent.category !== 'longevity') {
        return false;
      }
      
      // Must have sessions available
      if (talent.currentSession >= talent.maxSession) return false;
      
      // Check if talent is unlocked/available
      const talentsWithSessions = new Set(tree.filter(t => t.currentSession > 0 || t.id === 'health-tree').map(t => t.id));
      return isTalentUnlocked(talent.id, talentsWithSessions);
    });

    // Sort talents: if longevity complete, prioritize by tree then ranking
    const sortedTalents = [...availableTalents].sort((a, b) => {
      if (allLongevityMaxed) {
        // After longevity is complete: prioritize healthspan, then wellness
        const treePriority = { 'longevity': 1, 'healthspan': 2, 'wellness': 3 };
        const categoryA = treePriority[a.category] || 999;
        const categoryB = treePriority[b.category] || 999;
        
        if (categoryA !== categoryB) {
          return categoryA - categoryB;
        }
      }
      
      // Within same category (or if longevity not complete), sort by ranking
      const rankA = a.ranking ?? 999;
      const rankB = b.ranking ?? 999;
      return rankA - rankB;
    });

    console.log(`DEBUG: Available talents for recommendations (${allLongevityMaxed ? 'all categories' : 'longevity only'}):`, sortedTalents.map(t => ({ 
      id: t.id, 
      name: t.name, 
      ranking: t.ranking, 
      category: t.category,
      currentSession: t.currentSession,
      maxSession: t.maxSession,
      timeRequired: t.timeRequired
    })));

    // Progressive strategy:
    // - First: Complete ALL longevity talents
    // - Then: Move to healthspan talents  
    // - Finally: Move to wellness talents
    for (const talent of sortedTalents) {
      if (remainingMinutes <= 0) break;

      // Calculate how many sessions we can afford
      const sessionsAvailable = talent.maxSession - talent.currentSession;
      const maxAffordableSessions = Math.floor(remainingMinutes / talent.timeRequired);
      
      if (maxAffordableSessions <= 0 || sessionsAvailable <= 0) continue;

      let suggestedSessions: number;
      let reason: 'high-priority' | 'time-fill' | 'user-selected';

      // Special case for rank 1 (Sleep Hygiene): only give 1 session
      if (talent.ranking === 1) {
        suggestedSessions = Math.min(1, sessionsAvailable, maxAffordableSessions);
        reason = 'high-priority';
      } else {
        // For all other talents: give ALL available sessions (max them out)
        suggestedSessions = Math.min(sessionsAvailable, maxAffordableSessions);
        // Longevity = high-priority, others = time-fill
        reason = talent.category === 'longevity' ? 'high-priority' : 'time-fill';
      }

      if (suggestedSessions > 0) {
        const totalMinutes = suggestedSessions * talent.timeRequired;

        console.log(`DEBUG: Adding recommendation - ${talent.name} (${talent.category}, rank: ${talent.ranking ?? 999}) - ${suggestedSessions}/${talent.maxSession} sessions (${totalMinutes}min)`);

        recommendations.push({
          talent,
          suggestedSessions,
          totalMinutes,
          reason
        });

        remainingMinutes -= totalMinutes;
      }
    }

    console.log(`DEBUG: Final recommendations: ${recommendations.length} talents, ${availableMinutes - remainingMinutes}/${availableMinutes} minutes used`);
    return recommendations;
  }

  // Calculate total time spent across all talents
  getTotalSpentTime(): number {
    const tree = this.getTree();
    return tree.reduce((total, talent) => {
      return total + (talent.currentSession * talent.timeRequired);
    }, 0);
  }

  // Get time statistics
  getTimeStats(): TimeStats {
    const weeklyCommitment = this.getWeeklyCommitment();
    const totalSpentTime = this.getTotalSpentTime();
    const availableTime = Math.max(0, weeklyCommitment * 60 - totalSpentTime); // Convert hours to minutes

    return {
      weeklyCommitment,
      totalSpentTime,
      availableTime
    };
  }

  // Weekly commitment management
  setWeeklyCommitment(hours: number): void {
    localStorage.setItem('healthTreeWeeklyCommitment', hours.toString());
    this.notifyListeners();
  }

  getWeeklyCommitment(): number {
    const saved = localStorage.getItem('healthTreeWeeklyCommitment');
    return saved ? parseFloat(saved) : 6; // Default 6 hours
  }

  // Save tree state to localStorage
  private saveState(tree: TalentNode[]): void {
    const savedState: SavedTalentState[] = tree.map(talent => ({
      id: talent.id,
      currentSession: talent.currentSession,
      unlocked: talent.unlocked
    }));

    localStorage.setItem('unifiedTalentTreeState', JSON.stringify(savedState));
  }

  // Load tree state from localStorage
  private getSavedState(): SavedTalentState[] | null {
    const saved = localStorage.getItem('unifiedTalentTreeState');
    if (!saved) return null;

    try {
      return JSON.parse(saved);
    } catch (error) {
      console.error('Error parsing saved talent tree state:', error);
      return null;
    }
  }

  // Reset all talent progress
  resetAllProgress(): void {
    localStorage.removeItem('unifiedTalentTreeState');
    
    // Force reload the tree to reset all progress
    const tree = this.initializeTree();
    
    // Reset all talents to their default state (no sessions, only health-tree unlocked)
    tree.forEach(talent => {
      talent.currentSession = 0;
      talent.unlocked = talent.id === 'health-tree'; // Only health-tree starts unlocked
    });
    
    // Save the reset state
    this.saveState(tree);
    this.notifyListeners();
  }

  // Event system for UI updates
  addListener(callback: (stats: TimeStats) => void): void {
    this.listeners.push(callback);
  }

  removeListener(callback: (stats: TimeStats) => void): void {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  private notifyListeners(): void {
    const stats = this.getTimeStats();
    this.listeners.forEach(listener => listener(stats));
  }

  // Utility methods for talent information
  getTalentInfo(talentId: string): TalentNode | null {
    const tree = this.getTree();
    return tree.find(t => t.id === talentId) || null;
  }

  // Check if a talent can be selected for weekly commitment
  canSelectForCommitment(talentId: string): boolean {
    const tree = this.getTree();
    const talent = tree.find(t => t.id === talentId);
    
    if (!talent) return false;

    const talentsWithPoints = new Set(
      tree.filter(t => t.currentSession > 0).map(t => t.id)
    );

    return isTalentUnlocked(talentId, talentsWithPoints);
  }

  // Get talents by category for visualization
  getTalentsByCategory(): Record<string, TalentNode[]> {
    const tree = this.getTree();
    
    return {
      longevity: tree.filter(t => t.category === 'longevity'),
      healthspan: tree.filter(t => t.category === 'healthspan'),
      wellness: tree.filter(t => t.category === 'wellness')
    };
  }
}

export const unifiedTalentTreeService = UnifiedTalentTreeService.getInstance();
export default unifiedTalentTreeService;